import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { useAuth } from '@/features/auth';
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { projectDetailsQuery } from '@/features/projects/queries';
import {
  PROJECT_TYPE_DESCRIPTIONS_TEXT,
  PROJECT_TYPES,
  ProjectModeEnum,
  ProjectType,
} from '@/features/projects/constants';
import {
  Field,
  FieldControl,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '@/ui/components/form/field';
import { Button } from '@/ui/components/button';
import { Form } from '@/ui/components/form';
import { Card } from '@/ui/components/card';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectPopup,
  SelectItem,
  SelectTriggerIcon,
} from '@/ui/components/select';
import { H3 } from '@/ui/components/heading';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogPopup,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/ui/components/alert-dialog';
import {
  useArchiveProject,
  useDeleteProject,
  useUpdateProject,
} from '@/features/projects/mutations';
import { useNavigate } from '@tanstack/react-router';
import { useParams } from '@tanstack/react-router';
import { CountryCombobox } from '@/ui/components/country-combobox';
import { ProjectTypeIcon } from '@/features/projects/components/project-type-icon';
import { companiesQuery } from '@/features/companies/queries';
import { Company } from '@/features/companies/types';
import { CompanyCombobox } from '@/features/companies/components/company-combobox';
import type { WorkspaceCreatorType } from '@/features/workspaces/constants';
import { useSetProjectOpen, useSetProjectRestricted } from '@/features/projects/mutations';
import { canManageProject, toProjectType } from '@/features/projects/utils';

import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/_authed/project/$projectId/settings/')({
  component: RouteComponent,
});

function RouteComponent() {
  const { projectId } = useParams({ from: '/_authed/project/$projectId' });
  const { data: project } = useSuspenseQuery(projectDetailsQuery(projectId));
  const auth = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canManage = canManageProject(project, auth.user.organizationRole);

  const [nameValue, setNameValue] = useState(project.name);
  const [addressValue, setAddressValue] = useState(project.address ?? '');
  const [countryCodeValue, setCountryCodeValue] = useState(project.countryCode ?? null);
  const [creatorType, setCreatorType] = useState<WorkspaceCreatorType>(
    project.companyId ? 'supplier' : 'client',
  );
  const [pickedCompany, setPickedCompany] = useState<Company | null>();

  const isSupplier = creatorType === 'supplier';

  const { data: companies = [] } = useQuery({
    ...companiesQuery(auth.user.organizationId),
    enabled: isSupplier,
  });
  const selectedCompany =
    pickedCompany === undefined
      ? (companies.find((company) => company.id === project.companyId) ?? null)
      : pickedCompany;

  const creatorTypes = [
    { value: 'client', label: t({ en: 'Client', nb: 'Kunde' }) },
    { value: 'supplier', label: t({ en: 'Supplier', nb: 'Leverandør' }) },
  ];

  const [typeValue, setTypeValue] = useState(toProjectType(project.type));
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [restrictDialogOpen, setRestrictDialogOpen] = useState(false);
  const [openDialogOpen, setOpenDialogOpen] = useState(false);

  const updateProject = useUpdateProject();
  const deleteProject = useDeleteProject(project.id);
  const archiveProject = useArchiveProject(project.id, project.isSoftArchived ?? false);
  const restrictProject = useSetProjectRestricted();
  const openProject = useSetProjectOpen();

  return (
    <Form
      onFormSubmit={() =>
        updateProject.mutate({
          name: nameValue,
          address: addressValue,
          companyId: isSupplier ? (selectedCompany?.id ?? null) : null,
          countryCode: countryCodeValue ?? undefined,
          type: typeValue ?? project.type,
        })
      }
      className="max-w-xl w-full flex flex-col gap-4"
    >
      <Card className="space-y-4 p-4">
        <H3 className="-mt-1">{t({ en: 'Project information', nb: 'Prosjektinformasjon' })}</H3>
        <Field>
          <FieldLabel>{t({ en: 'Name', nb: 'Navn' })}</FieldLabel>
          <FieldControl
            required={canManage}
            value={nameValue}
            onValueChange={setNameValue}
            placeholder={t({ en: 'Name', nb: 'Navn' })}
            maxLength={60}
            readOnly={!canManage}
          />
          <FieldError />
          <FieldDescription>
            {t({
              en: 'This is your public display name for your organisation. It can be your real name or a pseudonym.',
              nb: 'Dette er dit offentlige visningsnavn for organisasjonen. Det kan være ditt riktige navn eller et pseudonym.',
            })}
          </FieldDescription>
        </Field>
        <Field>
          <FieldLabel>{t({ en: 'Project type', nb: 'Prosjekttype' })}</FieldLabel>
          <Select
            required
            readOnly={!canManage}
            value={typeValue ?? null}
            onValueChange={(v) => {
              if (!v) return;
              setTypeValue(v);
            }}
          >
            <SelectTrigger className="w-full pl-2" size="lg">
              <SelectValue>
                {(v) => (
                  <>
                    <ProjectTypeIcon type={v} />
                    {v ? t(common.projectType[v as ProjectType]) : project.type}
                  </>
                )}
              </SelectValue>
              <SelectTriggerIcon />
            </SelectTrigger>
            <SelectPopup>
              {PROJECT_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  <ProjectTypeIcon type={type} />
                  {t(common.projectType[type])}
                </SelectItem>
              ))}
            </SelectPopup>
          </Select>
          <FieldError />
          <FieldDescription>
            {typeValue && t(PROJECT_TYPE_DESCRIPTIONS_TEXT[typeValue])}
          </FieldDescription>
        </Field>
        <Field>
          <FieldLabel>{t({ en: 'Country', nb: 'Land' })}</FieldLabel>
          <CountryCombobox
            readOnly={!canManage}
            value={countryCodeValue}
            onValueChange={setCountryCodeValue}
            ariaLabel={t({ en: 'Country', nb: 'Land' })}
            placeholder={t({ en: 'Select country...', nb: 'Velg land...' })}
          />
          <FieldError />
        </Field>
        <Field>
          <FieldLabel>{t({ en: 'Address', nb: 'Adresse' })}</FieldLabel>
          <FieldControl
            maxLength={200}
            value={addressValue}
            onValueChange={setAddressValue}
            placeholder={t({ en: 'Address', nb: 'Adresse' })}
            readOnly={!canManage}
          />
          <FieldError />
        </Field>
        <Field>
          <FieldLabel>
            {t({ en: "Your organisation's role", nb: 'Organisasjonens rolle' })}
          </FieldLabel>
          <Select
            readOnly={!canManage}
            value={creatorType}
            onValueChange={(value) => {
              if (!value) return;
              setCreatorType(value as WorkspaceCreatorType);
            }}
          >
            <SelectTrigger size="lg" className="w-full">
              <SelectValue>
                {(v) => creatorTypes.find((item) => item.value === v)?.label}
              </SelectValue>
              <SelectTriggerIcon />
            </SelectTrigger>
            <SelectPopup>
              {creatorTypes.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectPopup>
          </Select>
          <FieldError />
        </Field>
        {isSupplier && (
          <Field>
            <FieldLabel>{t({ en: 'Who is the client?', nb: 'Hvem er kunden?' })}</FieldLabel>
            <CompanyCombobox
              required
              companies={companies}
              value={selectedCompany}
              onValueChange={setPickedCompany}
              disabled={!canManage}
              defaultInputValue={selectedCompany?.name}
              ariaLabel={t({ en: 'Who is the client?', nb: 'Hvem er kunden?' })}
            />
            <FieldError />
          </Field>
        )}
      </Card>
      {canManage ? (
        <Button type="submit" className="ml-auto" pending={updateProject.isPending}>
          {t({ en: 'Update project', nb: 'Oppdater prosjekt' })}
        </Button>
      ) : null}
      {canManage && project.mode === ProjectModeEnum.RESTRICTED && (
        <Card className="p-4">
          <H3 className="-mt-1">{t({ en: 'Open project', nb: 'Åpne prosjekt' })}</H3>
          <p className="text-sm text-muted mt-2 mb-4">
            {t({
              en: ' This project will be open and all members of your organisation will have access to it. ',
              nb: 'Dette prosjektet vil være åpent, og alle medlemmer av din organisasjon vil ha tilgang til det.',
            })}
          </p>
          <Button variant="secondary" onClick={() => setOpenDialogOpen(true)}>
            {t({ en: 'Open project', nb: 'Åpne prosjekt' })}
          </Button>
          <AlertDialog open={openDialogOpen} onOpenChange={setOpenDialogOpen}>
            <AlertDialogPopup>
              <AlertDialogTitle>{t({ en: 'Open project', nb: 'Åpne prosjekt' })}</AlertDialogTitle>
              <AlertDialogDescription>
                {t({
                  en: ' This project will be open and all members of your organisation will have access to it. ',
                  nb: 'Dette prosjektet vil være åpent, og alle medlemmer av din organisasjon vil ha tilgang til det.',
                })}
              </AlertDialogDescription>
              <AlertDialogFooter>
                <AlertDialogCancel />
                <Button
                  onClick={() =>
                    openProject.mutate(undefined, {
                      onSuccess: () => setOpenDialogOpen(false),
                    })
                  }
                  pending={openProject.isPending}
                  disabled={openProject.isPending}
                >
                  {t({ en: 'Open', nb: 'Åpne' })}
                </Button>
              </AlertDialogFooter>
            </AlertDialogPopup>
          </AlertDialog>
        </Card>
      )}
      {canManage ? (
        <>
          {project.mode === ProjectModeEnum.OPEN && (
            <Card className="p-4">
              <H3 className="-mt-1">{t({ en: 'Restrict project', nb: 'Begrens prosjekt' })}</H3>
              <p className="text-sm text-muted mt-2 mb-4">
                {t({
                  en: 'This project will be restricted and only selected members will have access to it.',
                  nb: 'Dette prosjektet vil bli begrenset, og kun utvalgte medlemmer vil ha tilgang til det.',
                })}
              </p>
              <Button variant="secondary" onClick={() => setRestrictDialogOpen(true)}>
                {t({ en: 'Restrict project', nb: 'Begrens prosjekt' })}
              </Button>
              <AlertDialog open={restrictDialogOpen} onOpenChange={setRestrictDialogOpen}>
                <AlertDialogPopup>
                  <AlertDialogTitle>
                    {t({ en: 'Restrict project', nb: 'Begrens prosjekt' })}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t({
                      en: 'This project will be restricted and only selected members will have access to it.',
                      nb: 'Dette prosjektet vil bli begrenset, og kun utvalgte medlemmer vil ha tilgang til det.',
                    })}
                  </AlertDialogDescription>
                  <AlertDialogFooter>
                    <AlertDialogCancel />
                    <Button
                      onClick={() =>
                        restrictProject.mutate(undefined, {
                          onSuccess: () => setRestrictDialogOpen(false),
                        })
                      }
                      pending={restrictProject.isPending}
                      disabled={restrictProject.isPending}
                    >
                      {t({ en: 'Restrict', nb: 'Begrens' })}
                    </Button>
                  </AlertDialogFooter>
                </AlertDialogPopup>
              </AlertDialog>
            </Card>
          )}
          <Card className="p-4">
            <H3 className="-mt-1">
              {project.isSoftArchived
                ? t({ en: 'Restore project', nb: 'Gjenopprett prosjekt' })
                : t({ en: 'Archive project', nb: 'Arkiver prosjekt' })}
            </H3>
            <p className="text-sm text-muted mt-2 mb-4">
              {project.isSoftArchived
                ? t({
                    en: 'This project will be moved to active projects and will be visible in the project list. It will be fully accessible.',
                    nb: 'Dette prosjektet vil bli flyttet til aktive prosjekter og bli synlig i prosjektlisten. Det vil være fullt tilgjengelig.',
                  })
                : t({
                    en: 'This project will be moved to the archive and will be hidden in project list. You can restore it later.',
                    nb: 'Dette prosjektet vil bli flyttet til arkivet og skjult i prosjektlisten. Du kan gjenopprette det senere.',
                  })}
            </p>
            <Button variant="secondary" onClick={() => setArchiveDialogOpen(true)}>
              {project.isSoftArchived
                ? t({ en: 'Restore project', nb: 'Gjenopprett prosjekt' })
                : t({ en: 'Archive project', nb: 'Arkiver prosjekt' })}
            </Button>
            <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
              <AlertDialogPopup>
                <AlertDialogTitle>
                  {project.isSoftArchived
                    ? t({ en: 'Restore project', nb: 'Gjenopprett prosjekt' })
                    : t({ en: 'Archive project', nb: 'Arkiver prosjekt' })}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {project.isSoftArchived
                    ? t({
                        en: 'This project will be moved to active projects and will be visible in the project list. It will be fully accessible.',
                        nb: 'Dette prosjektet vil bli flyttet til aktive prosjekter og bli synlig i prosjektlisten. Det vil være fullt tilgjengelig.',
                      })
                    : t({
                        en: 'This project will be moved to the archive and will be hidden in project list. You can restore it later.',
                        nb: 'Dette prosjektet vil bli flyttet til arkivet og skjult i prosjektlisten. Du kan gjenopprette det senere.',
                      })}
                </AlertDialogDescription>
                <AlertDialogFooter>
                  <AlertDialogCancel />
                  <Button
                    onClick={() =>
                      archiveProject.mutate(undefined, {
                        onSuccess: () => {
                          queryClient.invalidateQueries(projectDetailsQuery(project.id));
                          setArchiveDialogOpen(false);
                        },
                      })
                    }
                    pending={archiveProject.isPending}
                    disabled={archiveProject.isPending}
                  >
                    {t({ en: 'Archive project', nb: 'Arkiver prosjekt' })}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogPopup>
            </AlertDialog>
          </Card>
          <Card className="p-4">
            <H3 className="-mt-1">{t({ en: 'Delete project', nb: 'Slett prosjekt' })}</H3>
            <p className="text-sm text-muted mt-2 mb-4">
              {t({
                en: 'Wow, approach with caution! There is no going back when you delete a project.',
                nb: 'Wow, vær forsiktig! Det er ingen vei tilbake når du sletter et prosjekt.',
              })}
            </p>
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <AlertDialogTrigger
                render={
                  <Button variant="destructive">
                    {t({ en: 'Delete project', nb: 'Slett prosjekt' })}
                  </Button>
                }
              />
              <AlertDialogPopup>
                <AlertDialogTitle>
                  {t({ en: 'Delete project?', nb: 'Slett prosjekt?' })}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t({
                    en: 'All saved Tenders, Contracts and others will be lost.',
                    nb: 'Alle lagrede anbud, kontrakter og annet vil gå tapt.',
                  })}
                </AlertDialogDescription>
                <AlertDialogFooter>
                  <AlertDialogCancel />
                  <Button
                    variant="destructive"
                    onClick={() =>
                      deleteProject.mutate(undefined, {
                        onSuccess: () => {
                          setDeleteDialogOpen(false);
                          navigate({ to: '/' });
                        },
                      })
                    }
                    pending={deleteProject.isPending}
                    disabled={deleteProject.isPending}
                  >
                    {t({ en: 'Delete project', nb: 'Slett prosjekt' })}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogPopup>
            </AlertDialog>
          </Card>
        </>
      ) : null}
    </Form>
  );
}
